import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@cloudgenie/ui';
import { API_BASE, useAuth, hasCap } from '../AuthContext';
import { OnboardingChecklist } from '../components/OnboardingChecklist';

/** Wave 5 Phase 5 — Admin console landing page.
 *  Lists every tenant with plan/status; owners can click into a detail view. */

interface TenantRow {
  tenant_id: string;
  name?: string | null;
  plan?: string | null;
  status?: string | null;
  seats?: number | null;
  created_at?: string | null;
}

export default function AdminPage() {
  const { user, loading: authLoading } = useAuth();
  const [rows, setRows] = useState<TenantRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [q, setQ] = useState('');

  useEffect(() => {
    if (authLoading || !user) return;
    if (!hasCap(user, 'admin.ops')) return;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/admin/tenants?limit=1000`, { credentials: 'include' });
        if (!res.ok) throw new Error(`${res.status}`);
        setRows((await res.json()) as TenantRow[]);
      } catch (e) {
        setError((e as Error).message);
      }
    })();
  }, [authLoading, user]);

  if (authLoading) {
    return <AdminChrome title="Admin console"><p className="text-center py-16 text-muted-foreground" data-testid="admin-loading">Loading…</p></AdminChrome>;
  }
  if (!user) {
    window.location.href = `/login?next=${encodeURIComponent('/admin')}`;
    return null;
  }
  if (!hasCap(user, 'admin.ops')) {
    return (
      <AdminChrome title="Admin console">
        <div className="max-w-lg mx-auto py-24 text-center" data-testid="admin-forbidden">
          <p className="text-lg mb-3">You don&apos;t have access to the admin console.</p>
          <p className="text-sm text-muted-foreground mb-6">
            Your role: <strong>{user.role}</strong>. Required capability: <code>admin.ops</code>.
          </p>
          <Link to="/"><Button variant="outline">Back to account</Button></Link>
        </div>
      </AdminChrome>
    );
  }

  const filtered = (rows || []).filter(r => {
    if (!q.trim()) return true;
    const needle = q.toLowerCase();
    return (
      (r.tenant_id || '').toLowerCase().includes(needle) ||
      (r.name || '').toLowerCase().includes(needle) ||
      (r.plan || '').toLowerCase().includes(needle)
    );
  });

  return (
    <AdminChrome title="Admin console">
      <section className="max-w-6xl mx-auto px-6 pt-12 pb-6" data-testid="admin-page">
        <div className="flex items-baseline justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-2">Admin</p>
            <h1 className="text-4xl sm:text-5xl font-semibold">Tenants</h1>
            <p className="text-muted-foreground mt-2">
              {rows === null ? 'Loading tenants…' : `${rows.length} tenants total`}
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/admin/users"><Button variant="outline" data-testid="admin-users-link">Users</Button></Link>
            <Link to="/admin/api-keys"><Button variant="outline" data-testid="admin-api-keys-link">API Keys</Button></Link>
            <Link to="/cli-authorize"><Button variant="outline" data-testid="admin-cli-authorize-link">CLI authorize</Button></Link>
          </div>
        </div>

        {error && <p role="alert" className="mt-6 text-sm text-red-400" data-testid="admin-error">{error}</p>}

        <OnboardingChecklist />

        <div className="mt-8">
          <input
            data-testid="admin-search"
            type="search"
            value={q}
            placeholder="Search by id, name or plan…"
            onChange={(e) => setQ(e.target.value)}
            className="w-full max-w-md rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </div>

        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-card">
          <table className="w-full text-sm" data-testid="admin-tenants-table">
            <thead className="text-xs uppercase tracking-widest text-muted-foreground">
              <tr>
                <th className="text-left px-4 py-3">Tenant ID</th>
                <th className="text-left px-4 py-3">Name</th>
                <th className="text-left px-4 py-3">Plan</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Seats</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows === null && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">Loading…</td></tr>
              )}
              {rows !== null && filtered.length === 0 && (
                <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No tenants match.</td></tr>
              )}
              {filtered.map((r) => (
                <tr key={r.tenant_id} className="border-t border-border/40 hover:bg-muted/40" data-testid={`tenant-row-${r.tenant_id}`}>
                  <td className="px-4 py-3 font-mono text-xs">{r.tenant_id}</td>
                  <td className="px-4 py-3">{r.name || <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-3"><PlanBadge plan={r.plan} /></td>
                  <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
                  <td className="px-4 py-3">{r.seats ?? '—'}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/admin/tenants/${r.tenant_id}`} data-testid={`tenant-open-${r.tenant_id}`} className="text-primary hover:underline">Open →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </AdminChrome>
  );
}

function PlanBadge({ plan }: { plan?: string | null }) {
  if (!plan) return <span className="text-muted-foreground">—</span>;
  const label = plan.replace('_', ' ');
  return <span className="inline-block rounded-full border border-border bg-muted/40 px-2 py-0.5 text-xs">{label}</span>;
}
function StatusBadge({ status }: { status?: string | null }) {
  if (!status) return <span className="text-muted-foreground">—</span>;
  const cls = status === 'active' ? 'border-emerald-700/50 text-emerald-300 bg-emerald-900/20'
            : status === 'suspended' ? 'border-yellow-700/50 text-yellow-200 bg-yellow-900/20'
            : 'border-red-700/50 text-red-300 bg-red-900/20';
  return <span className={`inline-block rounded-full border px-2 py-0.5 text-xs ${cls}`}>{status}</span>;
}

export function AdminChrome({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col" data-testid="shell-id-admin">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 h-14">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="inline-block w-6 h-6 rounded-md bg-primary" />
            <span className="font-semibold tracking-tight">CloudGenie · {title}</span>
          </Link>
          <div className="flex items-center gap-4 text-sm">
            <Link to="/admin" className="text-muted-foreground hover:text-foreground">Tenants</Link>
            <Link to="/admin/users" className="text-muted-foreground hover:text-foreground">Users</Link>
            <Link to="/admin/api-keys" className="text-muted-foreground hover:text-foreground">API Keys</Link>
            <Link to="/cli-authorize" className="text-muted-foreground hover:text-foreground">CLI</Link>
            <Link to="/" className="text-muted-foreground hover:text-foreground">← Account</Link>
          </div>
        </div>
      </header>
      <main className="flex-1">{children}</main>
    </div>
  );
}
